import { MENU_FLAT } from './menu.ts';
import { routePattern, routeTitle } from '../lib/routeTitles.ts';

/** TDI(탭 문서 인터페이스) 상태 (CR-011 2차). 두 화면(left/right)이 각각 탭 목록을 갖고, 탭은 화면 사이를 옮길 수 있다 */
export interface Tab { id: string; path: string; title: string; /** 마지막 활성 시각 (CR-048: 같은 패턴 탭이 여럿일 때 최근 것 재사용) */ at?: number }
export interface PaneTabs { tabs: Tab[]; active: string | null }
export type PaneKey = 'left' | 'right';
export interface TabsState { left: PaneTabs; right: PaneTabs }

export const newId = () => Math.random().toString(36).slice(2, 10);
export const emptyPane = (): PaneTabs => ({ tabs: [], active: null });
/** CR-048: 탭 제목은 라우트별 페이지 제목표. 표에 없으면 메뉴 라벨 → 경로. (h1 은 쓰지 않는다) */
export function titleFor(path: string, _fallback?: string | null) {
  const fixed = routeTitle(path); if (fixed) return fixed;
  const p = path.split('?')[0];
  const hit = [...MENU_FLAT].sort((a, b) => b.to.length - a.to.length).find((m) => (m.end ? p === m.to : p === m.to || p.startsWith(m.to + '/')));
  return hit?.label ?? p;
}
export function ensureTab(pane: PaneTabs, path: string): PaneTabs {
  if (pane.tabs.length && pane.active) return pane;
  const t = { id: newId(), path, title: titleFor(path) };
  return { tabs: [...pane.tabs, t], active: t.id };
}
export function openTab(pane: PaneTabs, path: string): PaneTabs {
  const t = { id: newId(), path, title: titleFor(path) };
  const i = pane.tabs.findIndex((x) => x.id === pane.active);
  const tabs = [...pane.tabs]; tabs.splice(i + 1, 0, t);
  return { tabs, active: t.id };
}
/** 저장된 탭 상태를 불러올 때 제목을 제목표로 다시 계산한다 (옛 h1 제목 잔재 제거, CR-048) */
export function retitle(pane: PaneTabs): PaneTabs { return { ...pane, tabs: pane.tabs.map((t) => ({ ...t, title: titleFor(t.path) })) }; }
export function updateActive(pane: PaneTabs, path: string, title?: string | null): PaneTabs {
  if (!pane.active) return ensureTab(pane, path);
  return { ...pane, tabs: pane.tabs.map((t) => (t.id === pane.active ? { ...t, path, title: titleFor(path, title) } : t)) };
}
/**
 * 화면 이동 시 누적 (CR-048):
 *  ① 경로(쿼리 제외)가 완전히 같은 탭 → 제자리 활성화
 *  ② 라우트 패턴이 같고 매개변수만 다른 탭(예 /policies/:id) → 경로·제목을 바꿔 활성화하고 맨 끝으로 이동 (여러 개면 가장 최근 활성)
 *  ③ 없으면 활성 탭 오른쪽에 새 탭
 */
export function visit(pane: PaneTabs, path: string, title?: string | null): PaneTabs {
  const key = path.split('?')[0]; const now = Date.now();
  const exact = pane.tabs.find((t) => t.path.split('?')[0] === key);
  if (exact) return { tabs: pane.tabs.map((t) => (t.id === exact.id ? { ...t, path, title: titleFor(path, title), at: now } : t)), active: exact.id };
  const pattern = routePattern(path);
  const same = pane.tabs.filter((t) => routePattern(t.path) === pattern).sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
  if (same.length) {
    const hit = same[0];
    const moved = { ...hit, path, title: titleFor(path, title), at: now };
    return { tabs: [...pane.tabs.filter((t) => t.id !== hit.id), moved], active: hit.id };
  }
  const t = { id: newId(), path, title: titleFor(path, title), at: now };
  const i = pane.tabs.findIndex((x) => x.id === pane.active);
  const tabs = [...pane.tabs]; tabs.splice(i + 1, 0, t);
  return { tabs, active: t.id };
}
export function closeTab(pane: PaneTabs, id: string): PaneTabs {
  const i = pane.tabs.findIndex((t) => t.id === id);
  if (i < 0) return pane;
  const tabs = pane.tabs.filter((t) => t.id !== id);
  const active = pane.active === id ? (tabs[i] ?? tabs[i - 1])?.id ?? null : pane.active;
  return { tabs, active };
}
export function moveTab(state: TabsState, from: PaneKey, id: string, to: PaneKey, index?: number): TabsState {
  const tab = state[from].tabs.find((t) => t.id === id);
  if (!tab) return state;
  const src = closeTab(state[from], id);
  const dstTabs = [...state[to].tabs];
  if (from === to) { const j = index ?? dstTabs.length; const cur = dstTabs.findIndex((t) => t.id === id); dstTabs.splice(cur, 1); dstTabs.splice(Math.min(j > cur ? j - 1 : j, dstTabs.length), 0, tab); return { ...state, [to]: { tabs: dstTabs, active: id } }; }
  dstTabs.splice(index ?? dstTabs.length, 0, tab);
  return { ...state, [from]: src, [to]: { tabs: dstTabs, active: id } };
}
export const activeTab = (pane: PaneTabs) => pane.tabs.find((t) => t.id === pane.active) ?? null;
